import BaseStockProvider from '../BaseStockProvider';

/**
 * Freesound stock audio provider
 * Free API: https://freesound.org/docs/api/
 * 
 * Freesound API Guidelines:
 * 1. Attribute Freesound and the sound creator
 * 2. Display "Sound by [Creator] from Freesound"
 * 3. Link back to the sound page on Freesound
 * 4. Rate limit: Varies by authentication method
 */
class FreesoundProvider extends BaseStockProvider {
  constructor(apiKey = null) {
    super({
      name: 'Freesound',
      apiKey: apiKey || process.env.REACT_APP_FREESOUND_API_KEY,
      baseUrl: 'https://freesound.org/apiv2',
      mediaTypes: ['audio'],
      requiresAuth: true,
    });
  }

  /**
   * Search for audio on Freesound
   */
  async search({ query, page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Freesound API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      // Filter out explicit/sensitive content by excluding common sensitive tags
      const filter = '-tag:explicit -tag:nsfw -tag:adult -tag:sex -tag:sexual -tag:porn -tag:profanity -tag:offensive -tag:violence -tag:violent -tag:gore';
      
      const response = await fetch(
        `${this.baseUrl}/search/text/?query=${encodeURIComponent(query)}&filter=${encodeURIComponent(filter)}&page=${page}&page_size=${perPage}&fields=id,name,tags,description,duration,previews,username,license,url,download&token=${this.apiKey}`
      );

      if (!response.ok) {
        throw new Error(`Freesound API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Filter out sensitive content first (before normalization)
      const safeResults = this.filterSafeContent(data.results);
      
      // Normalize and filter out invalid formats
      const normalizedItems = safeResults.map(item => this.normalizeItem(item, 'audio'));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: !!data.next,
        total: data.count,
        page: page,
      };
    } catch (error) {
      console.error('Freesound search error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Get popular audio (Freesound doesn't have a dedicated popular endpoint, use general search sorted by rating)
   */
  async getPopular({ page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Freesound API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      // Freesound doesn't support wildcard queries, so we search for common terms
      // and sort by rating to get popular sounds
      // Filter out explicit/sensitive content by excluding common sensitive tags
      const filter = '-tag:explicit -tag:nsfw -tag:adult -tag:sex -tag-sex-moan -tag:sexual -tag:porn -tag:profanity -tag:offensive -tag:violence -tag:violent -tag:gore';
      
      const response = await fetch(
        `${this.baseUrl}/search/text/?query=music&filter=${encodeURIComponent(filter)}&sort=rating_desc&page=${page}&page_size=${perPage}&fields=id,name,tags,description,duration,previews,username,license,url,download&token=${this.apiKey}`
      );

      if (!response.ok) {
        throw new Error(`Freesound API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Filter out sensitive content first (before normalization)
      const safeResults = this.filterSafeContent(data.results);
      
      // Normalize and filter out invalid formats
      const normalizedItems = safeResults.map(item => this.normalizeItem(item, 'audio'));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: !!data.next,
        total: data.count,
        page: page,
      };
    } catch (error) {
      console.error('Freesound getPopular error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Check if content contains sensitive/explicit keywords
   * @param {Object} item - Freesound item
   * @returns {boolean} - True if content is safe, false if sensitive
   */
  isSafeContent(item) {
    // Focus on truly inappropriate content, not legitimate sound effects
    const sensitiveKeywords = [
      'explicit', 'nsfw', 'adult-content', 'sex', 'sexual', 'porn', 'pornographic',
      'profanity', 'erotic', 'nude', 'naked', 'xxx', 'rape', 'abuse',
      'fuck', 'fucking', 'shit', 'bitch', 'motherfucker', 'asshole'
    ];

    // Check tags
    if (item.tags && Array.isArray(item.tags)) {
      const tagsLower = item.tags.map(tag => tag.toLowerCase());
      if (tagsLower.some(tag => sensitiveKeywords.some(keyword => tag.includes(keyword)))) {
        return false;
      }
    }

    // Check name
    const nameLower = (item.name || '').toLowerCase();
    if (sensitiveKeywords.some(keyword => nameLower.includes(keyword))) {
      return false;
    }

    // Check description
    const descLower = (item.description || '').toLowerCase();
    if (sensitiveKeywords.some(keyword => descLower.includes(keyword))) {
      return false;
    }

    return true;
  }

  /**
   * Filter out items with sensitive content
   * @param {Array} items - Array of Freesound items
   * @returns {Array} - Filtered array
   */
  filterSafeContent(items) {
    return items.filter(item => {
      const isSafe = this.isSafeContent(item);
      if (!isSafe) {
        console.log(`Filtered out Freesound item due to sensitive content:`, {
          name: item.name,
          tags: item.tags,
          id: item.id
        });
      }
      return isSafe;
    });
  }

  /**
   * Normalize Freesound item to common format
   */
  normalizeItem(item, mediaType) {
    return {
      id: `freesound_${item.id}`,
      provider: 'freesound',
      name: item.name,
      thumbnail: null, // Freesound doesn't provide thumbnails for audio
      url: item.previews?.['preview-hq-mp3'] || item.previews?.['preview-lq-mp3'],
      downloadUrl: item.download, // Requires authentication to download
      type: mediaType,
      category: 'Stock',
      description: item.description || item.tags?.join(', '),
      size: null, // Freesound doesn't provide file size in search results
      duration: item.duration,
      author: {
        name: item.username,
        username: item.username,
        profileUrl: `https://freesound.org/people/${item.username}/`,
      },
      sourceUrl: item.url || `https://freesound.org/people/${item.username}/sounds/${item.id}/`,
      attribution: `Sound by ${item.username} from Freesound`,
      attributionUrl: item.url || `https://freesound.org/people/${item.username}/sounds/${item.id}/`,
      isUserUpload: false,
      license: item.license,
      tags: item.tags,
    };
  }

  /**
   * Get attribution text for display
   */
  getAttributionText(item) {
    return item.attribution || `Sound by ${item.author?.name || 'Unknown'} from Freesound`;
  }
}

export default FreesoundProvider;